/**
 * The questions the workflow's pickers offer.
 *
 * Reported from production: a field added to a draft showed up on the Logic
 * page and not here. It is the same underlying split — `GET /:id/builder`
 * returns the PUBLISHED catalog, and the draft is somewhere else — but the two
 * pages want different answers to it, so this is not simply Logic's code moved.
 *
 * Logic edits the draft and publishes with it, so the draft alone is the truth
 * there. The workflow is not behind Publish: it runs against submissions that
 * have already arrived, which carry only what was published when they were
 * sent. So the answer here is the UNION, with the draft-only ones marked — and
 * `validateGraph` warns about a branch that tests one, because until the form
 * publishes it, every submission takes the other path.
 */
import type { FieldDefinition } from '../../../shared/types';
import { questionsIn } from '../builderModel';
import { NOT_PUBLISHED_FLAG } from '../fieldGroups';
import type { BField } from '../builderModel';

/**
 * A draft row, read as a question.
 *
 * Deliberately NOT `toFieldBody`, which the Logic page uses. That builds a
 * publishable body and expects a fully normalised `BField` — every optional
 * string present, so it can `.trim()` them — and a draft one property short
 * takes the whole tab down with it, which is how this was found. A picker
 * needs a name, a type and a scope; none of that is worth a white screen.
 */
export function draftQuestions(fields: BField[] | undefined): FieldDefinition[] {
  return questionsIn(fields || []).map((f, i) => ({
    id: f.id ?? 0,
    key: f.key || '',
    label: f.label || f.key || '',
    section: f.section || '',
    input_type: (f.type || 'text') as FieldDefinition['input_type'],
    options: (f.options || []) as FieldDefinition['options'],
    flow_applicability: (f.flow || 'both') as FieldDefinition['flow_applicability'],
    scope: f.scope || 'order',
    sort_order: (i + 1) * 10,
    required_default: f.req === 'whenShown',
    ui_meta: {},
  })).filter((f) => f.key);
}

/**
 * Everything a branch may name: what live submissions carry, plus what the
 * draft is about to add. Published wins on a clash — that is the version the
 * submissions in the table actually have.
 */
export function questionsForWorkflow(
  published: FieldDefinition[],
  draftFields: BField[] | undefined
): FieldDefinition[] {
  const live = new Set(published.map((f) => f.key));
  return [
    ...published,
    // Flagged rather than renamed, so every picker can put the note where it
    // reads — after the key, not between the name and it.
    ...draftQuestions(draftFields)
      .filter((f) => !live.has(f.key))
      .map((f) => ({ ...f, ui_meta: { ...f.ui_meta, [NOT_PUBLISHED_FLAG]: true } })),
  ];
}
